import React from 'react';
import { Paper, Box, Alert, Typography, useTheme } from '@mui/material';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { standardizeEmojis } from '../../utils/reportPostProcessor.js';

/**
 * FormattedContent - A reusable component for displaying LLM-generated content
 * with consistent styling across the application.
 * 
 * This component handles:
 * - Emoji standardization
 * - Markdown rendering with consistent styling
 * - Professional typography and spacing
 * - Cross-platform emoji font support
 * - Fixed letter spacing issues by aligning font stack with zigy-mobile
 */
export default function FormattedContent({
  content,
  showBorder = true,
  showBackground = false,
  padding = 3,
  enableEmojiStandardization = true,
  showProcessingInfo = false,
  processingWarnings = [],
  className = '',
  sx = {},
  ...props
}) {
  const theme = useTheme();
  // Process content for consistent display
  const processedContent = React.useMemo(() => {
    if (!content) return '';
    
    // Ensure content is a string
    let processed = typeof content === 'string' ? content : JSON.stringify(content);
    
    // Apply emoji standardization if enabled
    if (enableEmojiStandardization) {
      processed = standardizeEmojis(processed);
    }
    
    // Convert markdown to HTML
    const rawHTML = marked(processed);
    return DOMPurify.sanitize(rawHTML);
  }, [content, enableEmojiStandardization]);

  if (!content) return null;

  return (
    <Box>
      {/* Processing warnings */}
      {processingWarnings.length > 0 && (
        <Alert severity="warning" sx={{ marginBottom: 2 }}>
          <Typography variant="subtitle2">Content Processing Notes:</Typography>
          <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
            {processingWarnings.map((warning, index) => (
              <li key={index}>{warning}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* Processing info for development */}
      {showProcessingInfo && process.env.NODE_ENV === 'development' && (
        <Box sx={{ marginBottom: 1 }}>
          <Typography variant="caption" color="textSecondary">
            ������ FormattedContent: Emoji standardization {enableEmojiStandardization ? 'enabled' : 'disabled'}
          </Typography>
        </Box>
      )}

      <Paper
        elevation={0}
        className={className}
        sx={{
          background: showBackground ? theme.palette.background.default : 'transparent',
          padding: padding,
          borderRadius: 2,
          border: showBorder ? '1px solid' : 'none',
          borderColor: showBorder ? theme.palette.divider : 'transparent',
          overflowX: 'auto',
          
          // Enhanced styling for consistent, professional appearance
          '& h1, & h2, & h3, & h4, & h5, & h6': {
            textAlign: 'left',
            marginTop: '1.5em',
            marginBottom: '1em',
            fontWeight: 600,
            color: theme.palette.primary.main,
            '&:first-of-type': {
              marginTop: '0.5em'
            }
          },
          '& h1': {
            fontSize: '1.5em',
            paddingBottom: '0.3em'
          },
          '& h2': { fontSize: '1.3em' },
          '& h3': { fontSize: '1.1em' },
          '& h4': { fontSize: '1.05em' },
          '& h5': { fontSize: '1em' },
          '& h6': { fontSize: '0.95em' },
          
          // Paragraph and text styling
          '& p': {
            textAlign: 'left',
            lineHeight: 1.6,
            marginBottom: '1em',
            color: theme.palette.text.primary,
            '&:last-child': {
              marginBottom: 0
            }
          },
          
          // List styling
          '& ul, & ol': {
            textAlign: 'left',
            paddingLeft: '1.5em',
            marginBottom: '1em'
          },
          '& li': {
            marginBottom: '0.3em',
            lineHeight: 1.5
          },
          '& ul ul, & ol ol, & ul ol, & ol ul': {
            marginBottom: '0.5em'
          },
          
          // Table styling
          '& table': {
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: 0,
            marginBottom: '1.5em',
            fontSize: '0.875rem',
            border: '1px solid var(--table-border)',
            borderRadius: 'var(--radius-sm)',
            overflow: 'hidden'
          },
          '& th, & td': {
            textAlign: 'left',
            padding: 'var(--table-row-padding)',
            borderBottom: '1px solid var(--table-border)',
            verticalAlign: 'top'
          },
          '& th': {
            backgroundColor: 'var(--table-head-bg)',
            color: 'var(--table-head-text)',
            borderBottom: '1px solid var(--table-border)',
          },
          '& th strong': {
            fontWeight: 800,
          },
          '& tbody tr': {
            transition: 'background-color 0.15s ease',
          },
          '& tbody tr:nth-of-type(even)': {
            backgroundColor: 'var(--table-row-zebra-bg)',
          },
          '& tbody tr:last-child td': {
            borderBottom: 'none'
          },
          '& tbody tr:hover': {
            backgroundColor: 'var(--table-row-hover-bg)',
          },
          
          // Quote styling
          '& blockquote': {
            borderLeft: '4px solid',
            borderLeftColor: theme.palette.primary.main,
            paddingLeft: '1em',
            marginLeft: 0,
            marginRight: 0,
            fontStyle: 'italic',
            color: theme.palette.text.secondary,
            backgroundColor: theme.palette.action.hover,
            padding: '0.5em 1em',
            borderRadius: '0 var(--radius-md) var(--radius-md) 0'
          },
          
          // Code styling
          '& code': {
            backgroundColor: theme.palette.action.hover,
            padding: '2px 4px',
            borderRadius: '3px',
            fontSize: '0.9em',
            fontFamily: 'Monaco, Consolas, "Courier New", monospace',
            color: theme.palette.text.primary
          },
          '& pre': {
            backgroundColor: theme.palette.action.hover,
            padding: '1em',
            borderRadius: '4px',
            overflow: 'auto',
            fontSize: '0.85em',
            '& code': {
              backgroundColor: 'transparent',
              padding: 0,
              border: 'none'
            }
          },
          
          // Link styling
          '& a': {
            color: theme.palette.primary.main,
            textDecoration: 'none',
            '&:hover': {
              textDecoration: 'underline'
            }
          },
          
          // Horizontal rule styling
          '& hr': {
            border: 'none',
            borderTop: '1px solid var(--chat-formatted-border)',
            margin: '2em 0'
          },
          
          // Strong and emphasis styling
          '& strong, & b': {
            fontWeight: 600,
            color: theme.palette.text.primary
          },
          '& em, & i': {
            fontStyle: 'italic',
            color: theme.palette.text.secondary
          },
          
          // Image styling
          '& img': {
            maxWidth: '100%',
            height: 'auto',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          },
          
          // Font stack aligned with zigy-mobile for consistency
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji"',
          
          // Fix letter spacing issues caused by emoji fonts
          letterSpacing: 'normal',
          fontFeatureSettings: 'normal',
          textRendering: 'optimizeLegibility',
          
          // Custom styling overrides
          ...sx
        }}
        {...props}
      >
        <div dangerouslySetInnerHTML={{ __html: processedContent }} />
      </Paper>
    </Box>
  );
}
